import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The client for `apps/api`.
 *
 * TanStack Query is in the specification and will earn its place once the cache
 * is worth invalidating. Until then this is `fetch` plus a `useEffect`, with
 * `reload()` on every resource as the invalidation story: a voucher posts, the
 * register reloads, and that is the whole of it.
 *
 * Every amount in these types is a **decimal string**. Do not `Number()` one on
 * the way to the screen or on the way to the API; `formatMoney` and the
 * `decimal.ts` helpers take strings for the reason `packages/db/src/columns.ts`
 * returns them, and a JSON number would lose paisa in transit.
 */

const BASE = '/api';

// ── In flight ────────────────────────────────────────────────────────────────

/**
 * How many requests are outstanding, and who wants to know.
 *
 * Module-level rather than a context, for the same reason `reference-data.ts`
 * keeps the account registry there: `apiGet` is called from effects, event
 * handlers and plain functions, and none of them has a React tree to reach up
 * into. The shell subscribes; everything else simply makes requests.
 *
 * What it buys is the progress bar in the chrome — one answer to "did my click
 * do anything" for the whole application, rather than every screen inventing a
 * spinner. A counter rather than a boolean because screens fire three or four
 * reads at once and the bar must not clear when the first of them lands.
 */
let inFlight = 0;
const inFlightListeners = new Set<(count: number) => void>();

function settle(delta: number): void {
  inFlight += delta;
  for (const listener of inFlightListeners) listener(inFlight);
}

/**
 * True while the API is being talked to — after a short delay, so a read that
 * returns in 40ms shows nothing at all.
 *
 * The delay lives in `useDelayed` alongside the placeholder rows' one, so the
 * two loading indicators in the application are timed from the same place.
 */
export function useApiBusy(): boolean {
  const [count, setCount] = useState(inFlight);

  useEffect(() => {
    const listener = (next: number) => setCount(next);
    inFlightListeners.add(listener);
    // Between render and this effect a request may have started or finished.
    setCount(inFlight);
    return () => {
      inFlightListeners.delete(listener);
    };
  }, []);

  return count > 0;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The database's HINT, when a trigger supplied one. */
    readonly hint?: string,
  ) {
    super(message);
  }

  /** Message and hint as one sentence, for a callout. */
  get full(): string {
    return this.hint ? `${this.message} — ${this.hint}` : this.message;
  }
}

export async function apiGet<T>(
  path: string,
  params: Record<string, string | undefined> = {},
  signal?: AbortSignal,
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const suffix = query.toString();

  // `finally`, not a pair of calls around the happy path: an abort, a network
  // failure and a 500 all have to decrement, or the progress bar never clears
  // again for the life of the tab.
  settle(+1);
  try {
    const response = await fetch(`${BASE}${path}${suffix ? `?${suffix}` : ''}`, {
      signal,
      headers: { accept: 'application/json' },
      // The session is an HttpOnly cookie this code cannot read. `same-origin`
      // would be enough behind the Vite proxy and is not enough when the browser
      // app is served from a different origin than the API, which is the
      // ordinary production topology.
      credentials: 'include',
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: string;
        hint?: string;
      } | null;
      throw new ApiError(
        response.status,
        body?.error ?? `${response.status} ${response.statusText}`,
        body?.hint,
      );
    }
    return (await response.json()) as T;
  } finally {
    settle(-1);
  }
}

/**
 * A write. POST, PATCH or DELETE, JSON in, JSON out.
 *
 * The error path matters more than the success one here: when a posting is
 * refused it is refused by a trigger in `0015_invariants.sql` whose message was
 * written for an accountant to read, and this carries that message and its hint
 * through to the screen rather than replacing it with a status code.
 */
export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  params: Record<string, string | undefined> = {},
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const suffix = query.toString();

  settle(+1);
  try {
    const response = await fetch(`${BASE}${path}${suffix ? `?${suffix}` : ''}`, {
      method,
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : {
              accept: 'application/json',
              'content-type': 'application/json',
            },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
    });

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: string;
        hint?: string;
      } | null;
      throw new ApiError(
        response.status,
        payload?.error ?? `${response.status} ${response.statusText}`,
        payload?.hint,
      );
    }
    return (await response.json()) as T;
  } finally {
    settle(-1);
  }
}

export interface Resource<T> {
  data: T | undefined;
  error: string | undefined;
  /**
   * The HTTP status behind `error`, when there was a response at all.
   *
   * Undefined means the request never reached the API. The distinction matters:
   * a 404 from `/session` is a stale sign-in to be cleared, while a dead socket
   * is the API being down, and treating the second as the first would sign
   * somebody out every time they started the stack in the wrong order.
   */
  status: number | undefined;
  loading: boolean;
  reload: () => void;
}

/**
 * Fetches on mount and whenever the parameters change. Pass `path: null` to hold
 * off — the account ledger has nothing to ask for until an account is chosen.
 */
export function useResource<T>(
  path: string | null,
  params: Record<string, string | undefined> = {},
): Resource<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<number>();
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  // Params are compared by value: a fresh object literal on every render would
  // otherwise refetch forever.
  const key = JSON.stringify(params);
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const attempt = ++latest.current;
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    setStatus(undefined);

    apiGet<T>(path, JSON.parse(key) as Record<string, string | undefined>, controller.signal)
      .then((result) => {
        if (attempt !== latest.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || attempt !== latest.current) return;
        setError(
          cause instanceof ApiError
            ? cause.message
            : 'Cannot reach the API. Start it with `pnpm api`.',
        );
        setStatus(cause instanceof ApiError ? cause.status : undefined);
        setLoading(false);
      });

    return () => controller.abort();
  }, [path, key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, status, loading, reload };
}

// ── Shapes ───────────────────────────────────────────────────────────────────

export interface Session {
  id: string;
  username: string;
  email: string;
  fullName: string;
  initials: string;
  jobTitle: string | null;
  isSuperAdmin: boolean;
  roleCode: string;
  roleName: string;
  approvalLimitBase: string | null;
  canSelfApprove: boolean;
  /** `module:ACTION`, e.g. `finance.journal_voucher:POST`. */
  permissions: string[];
  branches: Array<{ code: string; name: string; isDefault: boolean }>;
  /**
   * Set when a super administrator has switched view, naming who is really at
   * the keyboard.
   *
   * The screen needs it: a top bar showing somebody else's role without saying
   * so is a screen that will eventually be screenshotted as evidence of
   * something that never happened. Everything above it — `fullName`, `roleCode`,
   * `permissions` — describes the *acted-as* user, which is what makes the
   * switch worth having.
   */
  viewingAs: { realEmail: string } | null;
  /**
   * The signed-in account is on a one-time password and has not replaced it.
   *
   * Describes the **real** user, never the acted-as one: "view as" is a way of
   * looking at somebody else's application, not of inheriting their obligation
   * to pick a password. While it is true the server refuses every route but a
   * small allowlist, so this is what the screen renders rather than what
   * enforces the rule.
   */
  mustChangePassword: boolean;
}

export interface ApiAccount {
  code: string;
  name: string;
  shortName: string | null;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  normalBalance: 'DR' | 'CR';
  parentCode: string | null;
  /** 0-based, as stored. `level` is the same number as an accountant says it. */
  depth: number;
  level: number;
  isLeaf: boolean;
  isControl: boolean;
  controlKind: string | null;
  allowDirectPosting: boolean;
  isContra: boolean;
  isSystem: boolean;
  /** The one foreign currency this account may hold, or null for base only. */
  fcyCurrency: string | null;
  requiresParty: boolean;
  statementGroup: string | null;
  cashFlowClass: string | null;
  isBank: boolean;
  isActive: boolean;
  notes: string | null;
  /** Sub-accounts. More than zero is why the row stopped taking voucher lines. */
  childCount: number;
  /** True once a line has posted: code, type and normal balance are frozen. */
  hasPostings: boolean;
  /**
   * Set on a level-5 detail account: whose sub-ledger this is.
   *
   * The chart's fifth level is not more structure, it is the people and
   * companies the fourth level summarises — one salesperson, one customer. These
   * three say which, so the screen can link back to them rather than leaving the
   * name to be parsed.
   */
  subLedgerKind: 'salesperson' | 'party' | null;
  subLedgerCode: string | null;
  subLedgerName: string | null;
  /** Signed: positive is a debit balance. Rolled up over descendants. */
  balanceBase: string;
  movements: number;
}

/** What the account write endpoints return. */
export interface AccountWriteResult {
  code: string;
  name: string;
  message: string;
}

export interface LedgerRow {
  date: string;
  voucherNo: string;
  voucherType: string;
  lineNo: number;
  status: string;
  partyName: string | null;
  particulars: string;
  debitBase: string;
  creditBase: string;
  signedBase: string;
  currency: string;
  debit: string;
  credit: string;
}

export interface LedgerResponse {
  account: {
    id: string;
    code: string;
    name: string;
    normalBalance: 'DR' | 'CR';
    type: string;
    isControl: boolean;
  };
  opening: string;
  rows: LedgerRow[];
}

export interface TrialBalanceRow {
  code: string;
  name: string;
  type: ApiAccount['type'];
  normalBalance: 'DR' | 'CR';
  depth: number;
  /** Balance brought forward — everything posted before the window opens. */
  openingDebitBase: string;
  openingCreditBase: string;
  /** Movement inside the window. */
  debitBase: string;
  creditBase: string;
  closingDebitBase: string;
  closingCreditBase: string;
  closingSide: string;
}

/** The trial balance and the window it was actually run for. */
export interface TrialBalanceResult {
  from: string | null;
  to: string | null;
  rows: TrialBalanceRow[];
}

export interface StatementRow {
  code: string;
  name: string;
  type: ApiAccount['type'];
  statementGroup: string | null;
  isContra: boolean;
  amountBase: string;
  /**
   * Balance sheet only: the synthetic row carrying the current year's result.
   *
   * `rpt.balance_sheet()` returns it with the account rows rather than leaving
   * the screen to fetch a P&L and add it up, so the transfer and the assets it
   * funds cannot be windowed differently. Absent on the profit and loss.
   */
  isResult?: boolean;
}

/** `/profit-and-loss?from=&to=` — the window comes back with the rows. */
export interface ProfitAndLossResult {
  from: string | null;
  to: string | null;
  rows: StatementRow[];
}

/** `/balance-sheet?asOn=` — the date comes back with the rows. */
export interface BalanceSheetResult {
  asOn: string | null;
  rows: StatementRow[];
}

export interface CashPositionRow {
  code: string;
  name: string;
  kind: 'CASH' | 'BANK';
  balanceBase: string;
  lastMovement: string | null;
}

export interface AgeingRow {
  partyCode: string;
  partyName: string;
  current: string;
  d30: string;
  d60: string;
  d90: string;
  d90plus: string;
  total: string;
}

export interface ApAgeingRow extends AgeingRow {
  origin: string;
}

export interface ApiParty {
  code: string;
  name: string;
  kind: string;
  isCustomer: boolean;
  isSupplier: boolean;
  isEmployee: boolean;
  isSubAgent: boolean;
  ntn: string | null;
  strn: string | null;
  isFiler: boolean;
  receivableAccount: string | null;
  payableAccount: string | null;
}

// ── The voucher register ─────────────────────────────────────────────────────

/** One row of the register the record navigator walks. */
export interface VoucherListRow {
  id: string;
  voucherNo: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  totalBase: string;
  lineCount: number;
  partyCode: string | null;
  partyName: string | null;
  isReversed: boolean;
}

export interface VoucherDetailLine {
  lineNo: number;
  accountCode: string;
  accountName: string;
  /** The head this account rolls into — null for a top-level account. */
  parentCode: string | null;
  parentName: string | null;
  side: 'Dr' | 'Cr';
  amount: string;
  debit: string;
  credit: string;
  debitBase: string;
  creditBase: string;
  partyCode: string | null;
  partyName: string | null;
  narration: string | null;
}

/**
 * Who did what to a voucher, and when.
 *
 * Both halves are nullable together and mean "this has not happened": a draft
 * has no `at` and no `by` for posting, and a voucher created by the seed has a
 * timestamp with no user behind it. The screen renders the pair or nothing —
 * a name with no date is not a fact anyone can act on.
 */
export interface AuditStamp {
  at: string | null;
  by: string | null;
}

export interface VoucherDetail {
  id: string;
  voucherNo: string;
  voucherType: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  currency: string;
  fxRate: string;
  partyCode: string | null;
  partyName: string | null;
  branchCode: string | null;
  branchName: string | null;
  /** `Period 07 · Jul 2026` — derived from the date, never chosen. */
  periodName: string | null;
  totalDebitBase: string;
  totalCreditBase: string;
  lineCount: number;
  createdAt: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  postedAt: string | null;
  postedByName: string | null;
  reversalReason: string | null;
  /** Set on a voucher that has been reversed: the REV that cancelled it. */
  reversedByNo: string | null;
  /** Set on a REV voucher: what it reverses. */
  reversalOfNo: string | null;
  lines: VoucherDetailLine[];
}

/** What `POST /api/vouchers` returns. */
export interface SavedVoucher {
  id: string;
  voucherNo: string;
  type: string;
  voucherDate: string;
  narration: string | null;
  status: string;
  totalDebitBase: string;
  totalCreditBase: string;
  lineCount: number;
  postedAt: string | null;
}

/**
 * What `DELETE /api/vouchers/:type/:no` returns.
 *
 * Two outcomes, because the ledger allows two: a draft is deleted, a posted
 * voucher is reversed. The caller does not choose — the server reports which
 * happened and the screen says so.
 */
export interface DeleteResult {
  action: 'DELETED' | 'REVERSED';
  voucherNo: string;
  reversalNo?: string;
  message: string;
}

export interface BulkRowResult {
  row: number;
  reference: string;
  ok: boolean;
  voucherNo?: string;
  status?: string;
  error?: string;
}

export interface BulkResult {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkRowResult[];
}

export interface ApiContext {
  /** The letterhead: what the top bar, every report header and a printed
   *  voucher all show, maintained in Setup → Company Profile. */
  company: {
    code: string;
    name: string;
    legalName: string;
    tagline: string | null;
    brandMark: string | null;
    logoUrl: string | null;
    ntn: string | null;
    strn: string | null;
    cuin: string | null;
    /** Which identifiers a printed document carries — Setup → Company Profile. */
    printShowNtn: boolean;
    printShowStrn: boolean;
    printShowCuin: boolean;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    province: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    reportFooter: string | null;
    baseCurrency: string;
    timezone: string;
    /** The unit a blank document line opens on. Null: use the product's own. */
    defaultUomCode: string | null;
  };
  fiscalYear: {
    code: string;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
  } | null;
  periods: Array<{
    periodNo: number;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
    isAdjustment: boolean;
  }>;
  /**
   * Every active currency in the register, base first, each with its live rate.
   *
   * This is what makes a currency added under Setup → Currencies selectable in
   * the top bar. `rate` is null when the register knows the currency and
   * `fx_rates` has no SPOT row for it yet.
   */
  currencies: Array<{
    code: string;
    name: string;
    symbol: string | null;
    decimals: number;
    isBase: boolean;
    rate: string | null;
  }>;
  /** Setup → Units of Measure. What every quantity picker offers. */
  uoms: Array<{ code: string; name: string; dimension: string; decimals: number }>;
  /** The live USD rate specifically — the import-costing screen's default. */
  usdRate: string | null;
}

// ── Management reports ───────────────────────────────────────────────────────
//
// Sales by zone, gross margin and the audit trail. Their entries in
// `design-data.ts` are gone — these read PostgreSQL.

export interface SalesByZoneRow {
  zoneCode: string | null;
  zoneName: string;
  salespersonCode: string | null;
  salespersonName: string;
  invoices: number;
  qtyBase: string;
  netSales: string;
  cogs: string;
  margin: string;
  marginPct: string | null;
  commission: string;
  /** Null where nobody has set a target — not zero. */
  targetValue: string | null;
  achievementPct: string | null;
}

export interface GrossMarginRow {
  sku: string;
  productName: string;
  categoryName: string | null;
  qtyBase: string;
  netSales: string;
  cogs: string;
  costPerUnit: string | null;
  ratePerUnit: string | null;
  marginPerUnit: string | null;
  margin: string;
  marginPct: string | null;
}

/**
 * One audited row's identity, for the list of what else one act touched.
 *
 * `entityId` is the surrogate key and is never rendered — it exists so two refs
 * can be told apart exactly. A reversal touches two vouchers and both describe
 * as the bare word `Voucher`.
 */
export interface AuditEntityRef {
  entityTable: string;
  entityId: string | null;
  entityKey: string | null;
  entityName: string | null;
}

export interface AuditTrailRow {
  loggedAt: string;
  username: string;
  action: string;
  /** The audited table, raw. `entityLabel()` words it. */
  entityTable: string;
  /** The surrogate key. Never rendered; it is what tells two refs apart. */
  entityId: string | null;
  /** The row's business key — `1-1-3-1-0001`, `CUS-0001`. Null on a line table. */
  entityKey: string | null;
  /** The row's own name, where the table is one a person maintains. */
  entityName: string | null;
  /** Set only where the audited row **is** a document. A master has none. */
  documentRef: string | null;
  amountBase: string | null;
  changedKeys: string[] | null;
  ipAddress: string | null;
  reason: string | null;
  /** How many log entries this row stands for. `1` under `?raw=1`. */
  changes: number;
  /** What the act touched, distinct, lead first, capped by the server. */
  touched: AuditEntityRef[];
  /** The true number of distinct rows touched, before the cap. */
  touchedCount: number;
}

// ── Dashboard ────────────────────────────────────────────────────────────────

/**
 * The whole Overview screen, from one request.
 *
 * Every panel is a different question about the same book on the same date, so
 * they arrive together and share one `asOf`. Eight separate requests would let a
 * voucher post between them and leave the KPI strip disagreeing with the trial
 * balance check sitting three inches below it.
 */
export interface DashboardKpi {
  key: 'cash' | 'receivables' | 'payables' | 'profit';
  label: string;
  valueBase: string;
  /** Signed, one decimal place. Null when there is no prior period to compare. */
  deltaPct: string | null;
  foot: string;
}

export interface DashboardResponse {
  asOf: string;
  fiscalYear: string | null;
  period: string | null;
  usdRate: string | null;
  kpis: DashboardKpi[];
  brokerage: {
    deal_value_mtd: string;
    deal_count_mtd: number;
    commission_mtd: string;
    commission_receivable: string;
    commission_invoice_count: number;
    unsettled: string;
  };
  trading: {
    net_sales_mtd: string;
    invoice_count_mtd: number;
    margin_mtd: string;
    stock_value: string;
    warehouse_count: number;
    in_transit: string;
    in_transit_lots: number;
    usd_payable: string;
  };
  /** Twelve weeks, oldest first. Weeks with no movement come back as zero. */
  cashBars: Array<{ week: string; receipts: string; payments: string }>;
  queue: {
    drafts: number;
    pending_approval: number;
    unmatched_bank: number;
    overdue_invoices: number;
    overdue_base: string;
    expiring_batches: number;
    unsettled_deals: number;
  };
  recent: Array<{
    voucherNo: string;
    voucherType: string;
    voucherDate: string;
    narration: string | null;
    partyName: string | null;
    totalBase: string;
    status: string;
  }>;
  /** `difference` is zero on a healthy book — the invariant asserts it per voucher. */
  trialBalance: { debits: string; credits: string; difference: string; unposted: number };
  stockAtRisk: Array<{
    sku: string;
    productName: string;
    batchNo: string;
    warehouseName: string;
    balanceQty: string;
    balanceValue: string;
    expiryDate: string | null;
    daysToExpiry: number | null;
    expiryStatus: string;
  }>;
}

// ── Users, roles and zones ───────────────────────────────────────────────────

/**
 * One row of the user directory.
 *
 * What is **not** in this list matters as much as what is. `GET /api/admin/users`
 * filters on the caller's rank in its WHERE clause, so an administrator's copy
 * of this array does not contain the super administrator at all — it is not
 * fetched and hidden, it is never selected. The screen therefore has no
 * "is this row visible" logic and could not reintroduce one.
 */
export interface AdminUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  initials: string | null;
  jobTitle: string | null;
  phone: string | null;
  status: string;
  roleCode: string;
  roleName: string;
  /** Lower is more powerful. Drives the indent on the hierarchy screen. */
  rank: number;
  approvalLimitBase: string | null;
  inheritsScope: boolean;
  scopeLevel: string;
  scopeName: string;
  scopeCode: string | null;
  branchCode: string | null;
  branchName: string | null;
  lastLoginAt: string | null;
  permissionCount: number;
  reportCount: number;
  canManage: boolean;
  /** Cannot be removed or switched off, whoever is asking. Refused by the API. */
  isSuperAdmin: boolean;
}

export interface AdminRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  rank: number;
  inheritsSubordinateScope: boolean;
  canSelfApprove: boolean;
  approvalLimitBase: string | null;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  permissionCount: number;
  canManage: boolean;
}

export interface PermissionModule {
  code: string;
  groupName: string;
  name: string;
  sortOrder: number;
  /** Which actions are meaningful here. A report has no POST. */
  allowedActions: string[];
}

export interface RolesResponse {
  roles: AdminRole[];
  modules: PermissionModule[];
  /** `roleCode → ['module:ACTION', …]`. The whole matrix in one payload. */
  grants: Record<string, string[]>;
  /** The caller's own rank and role name — what a new role must be created below. */
  callerRank: number;
  callerRoleName: string;
  /**
   * True when `permission_modules` holds modules the caller's own role does not.
   * `modules` and `grants` are filtered on the server, so the browser never
   * receives them — this flag is the only thing said about them, and it does not
   * say which.
   */
  hiddenModulesExist: boolean;
}

export interface AdminZone {
  id: string;
  code: string;
  name: string;
  parentCode: string | null;
  depth: number;
  /** `depth + 1` — the number a person says: 1 zone, 2 region, 3 territory. */
  level: number;
  path: string;
  managerId: string | null;
  managerName: string | null;
  isActive: boolean;
  childCount: number;
  /** Counted over the whole subtree, which is what a zone grant actually reaches. */
  userCount: number;
  customerCount: number;
  cityCount: number;
  cities: string[];
}

/** A city — `0044`. The level between a zone and the warehouses under it. */
export interface AdminCity {
  id: string;
  code: string;
  name: string;
  province: string | null;
  zoneCode: string | null;
  zoneName: string | null;
  isActive: boolean;
  /** Sales invoices filed under this city — what makes deleting one a decision. */
  invoiceCount: number;
}

export interface AdminWarehouse {
  id: string;
  code: string;
  name: string;
  kind: string;
  branchCode: string | null;
  addressLine1: string | null;
  phone: string | null;
  /** The chart row this warehouse's stock sits on, under `1-1-4 Inventory`. */
  inventoryAccount: string | null;
  inventoryAccountName: string | null;
  inTransitAccount: string | null;
  allowNegativeStock: boolean;
  isDefault: boolean;
  isActive: boolean;
  movementCount: number;
  documentCount: number;
}

/** `GET /api/admin/geography` — cities, warehouses and both their pickers. */
export interface GeographyRefs {
  cities: AdminCity[];
  warehouses: AdminWarehouse[];
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  warehouseKinds: string[];
  /** The account head a new warehouse's account is opened under, named. */
  stockHead: { code: string; name: string } | null;
  can: {
    city: { create: boolean; edit: boolean; delete: boolean };
    warehouse: { view: boolean; create: boolean; edit: boolean; delete: boolean };
  };
}

/** What the caller may do, answered by the server rather than guessed here. */
export interface AdminCaller {
  email: string;
  rank: number;
  roleName: string;
  isSuperAdmin: boolean;
  canCreateUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canEditRole: boolean;
  canCreateRole: boolean;
  canDeleteRole: boolean;
  canEditZone: boolean;
  canCreateZone: boolean;
  canDeleteZone: boolean;
}

export interface AdminRefs {
  roles: Array<{ code: string; name: string; rank: number; description: string | null }>;
  zones: Array<{ code: string; name: string; depth: number; path: string }>;
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  warehouses: Array<{ code: string; name: string }>;
  managers: Array<{ email: string; fullName: string }>;
  scopeLevels: string[];
  statuses: string[];
  caller: AdminCaller;
}

/** Every administration write answers with a sentence for the screen to show. */
export interface AdminWriteResult {
  message: string;
  /**
   * A generated one-time password, present only on creating a user and on
   * resetting one — and only in the response to the request that made it.
   *
   * Only the bcrypt hash is stored, so there is no route that can produce this
   * again. The screen has to show it prominently and say that it will not be
   * repeated, because an administrator who assumes they can look it up later
   * closes the panel without writing it down.
   */
  oneTimePassword?: string;
  /** Who the password above belongs to, so the screen can show the pair. */
  username?: string;
}

// ── Setup ────────────────────────────────────────────────────────────────────

export interface CompanyProfile {
  code: string;
  name: string;
  legalName: string;
  tagline: string | null;
  brandMark: string | null;
  /** Shown in the top bar and on every printed letterhead. */
  logoUrl: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  countryCode: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  baseCurrency: string;
  valuationMethod: string;
  timezone: string;
  fyStartMonth: number;
  fyStartDay: number;
  allowNegativeStock: boolean;
  enforceCreditLimit: boolean;
  makerCheckerEnabled: boolean;
  batchTracking: boolean;
  defaultBatchPick: string;
  /**
   * The unit a blank document line opens on (`0032`).
   *
   * Null means the book has no house unit and every entry screen falls back to
   * the product's own stock unit — a real answer, and the better one on a mixed
   * catalogue. Sent back as `''` to clear it.
   */
  defaultUomCode: string | null;
  reportFooter: string | null;
  /**
   * Which of the three identifiers a printed or exported report carries (`0039`).
   *
   * Not whether the company holds them — those are `ntn`, `strn` and `cuin`
   * above and stay editable whatever these say. Unticking one withholds the
   * number from paper; it does not clear it.
   */
  printShowNtn: boolean;
  printShowStrn: boolean;
  printShowCuin: boolean;
  isActive: boolean;
  /**
   * The two facts that decide what is still editable.
   *
   * Base currency locks once a voucher has posted and valuation method once
   * stock has moved — `trg_companies_policy_lock` refuses both. The form greys
   * the fields rather than letting somebody type into one and be refused.
   */
  hasPostings: boolean;
  hasStockMovement: boolean;
}

export interface FiscalYearRow {
  id: string;
  code: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  closedAt: string | null;
  closedByName: string | null;
  periodCount: number;
  voucherCount: number;
}

export interface PeriodRow {
  id: string;
  fiscalYearId: string;
  fiscalYearCode: string;
  periodNo: number;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  isAdjustment: boolean;
  closedAt: string | null;
  closedByName: string | null;
  reopenedAt: string | null;
  reopenedByName: string | null;
  reopenReason: string | null;
  /** What a close is closing. Zero is a formality; four hundred is a decision. */
  postedCount: number;
  draftCount: number;
}

export interface NumberSeriesRow {
  id: string;
  docType: string;
  prefix: string;
  separator: string;
  yearToken: string | null;
  padding: number;
  nextValue: string;
  isGapless: boolean;
  isActive: boolean;
  fiscalYearCode: string | null;
  branchCode: string | null;
  branchName: string | null;
  /** Assembled the way the SQL function assembles it, so the preview cannot lie. */
  nextNumber: string;
  issued: number;
}

export interface TaxCodeRow {
  id: string;
  code: string;
  name: string;
  kind: string;
  computation: string;
  rate: string;
  fixedPerUnit: string | null;
  uomCode: string | null;
  appliesToFiler: boolean;
  nonFilerRate: string | null;
  accountCode: string | null;
  accountName: string | null;
  isWithholding: boolean;
  isRecoverable: boolean;
  statutorySection: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  /** Above zero, the rate is history: a change is a new row, not an edit. */
  documentCount: number;
}

export interface FxRateRow {
  id: string;
  rateDate: string;
  fromCurrency: string;
  toCurrency: string;
  rateType: string;
  rate: string;
  source: string | null;
  note: string | null;
  createdByName: string | null;
}

export interface BranchRow {
  id: string;
  code: string;
  name: string;
  addressLine1: string | null;
  city: string | null;
  phone: string | null;
  isHeadOffice: boolean;
  isActive: boolean;
  userCount: number;
  voucherCount: number;
  warehouseCount: number;
  seriesCount: number;
}

export interface CurrencyRow {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  /** Presentation only — every amount is NUMERIC(19,4) whatever this says. */
  decimals: number;
  isBase: boolean;
  isActive: boolean;
  voucherCount: number;
  rateCount: number;
  accountCount: number;
  latestRate: string | null;
}

export interface UomRow {
  id: string;
  code: string;
  name: string;
  dimension: string;
  decimals: number;
  /**
   * How many of the dimension's canonical unit one of this is — KG 1, MT 1000,
   * LTR 1, ML 0.001. Every product's conversion is derived from it, which is
   * what makes a unit added here transactable straight away rather than merely
   * listed by the pickers and refused at Post.
   */
  factorToBase: string;
  isActive: boolean;
  /** A unit a product is *based* in cannot change dimension or be switched off. */
  baseForProducts: number;
  usedByProducts: number;
  conversionCount: number;
  movementCount: number;
}

export interface SetupRefs {
  currencies: Array<{ code: string; name: string; symbol: string | null; isBase: boolean }>;
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  fiscalYears: Array<{ code: string; name: string; status: string }>;
  uoms: Array<{ code: string; name: string; dimension: string }>;
  accounts: Array<{ code: string; name: string }>;
}

// ── Banking ──────────────────────────────────────────────────────────────────

export interface BankAccountOption {
  /** The GL account code, which is how every other screen names a bank. */
  code: string;
  label: string;
  bankName: string;
  accountNumber: string | null;
  currency: string;
  isDefault: boolean;
}

export interface ReconLine {
  lineNo: number;
  txnDate: string;
  valueDate: string | null;
  description: string;
  reference: string | null;
  instrumentNo: string | null;
  debit: string;
  credit: string;
  signed: string;
  runningBalance: string | null;
  status: string;
  matchedVoucherNo: string | null;
  matchConfidence: string | null;
  ignoreReason: string | null;
}

/**
 * `GET /api/bank-reconciliation`.
 *
 * Everything the screen shows for one imported statement. `statement: null` is
 * the honest answer for a bank with nothing imported — the screen says so rather
 * than falling back to sample rows, which is why `design-data.ts` no longer
 * carries a `recon` entry.
 */
export interface ReconciliationView {
  accounts: BankAccountOption[];
  account: BankAccountOption | null;
  statement: {
    id: string;
    statementNo: string | null;
    fromDate: string;
    toDate: string;
    openingBalance: string;
    closingBalance: string;
    sourceFormat: string;
    sourceFileName: string | null;
    importedAt: string;
    importedBy: string | null;
  } | null;
  counts: {
    total: number;
    matched: number;
    suggested: number;
    unmatched: number;
    ignored: number;
    open: number;
  };
  totals: {
    statementBalance: string;
    ledgerBalance: string;
    difference: string;
    debitTotal: string;
    creditTotal: string;
  };
  lines: ReconLine[];
  lastReconciled: { date: string; balance: string; status: string } | null;
}

/**
 * `GET /api/nav-counts` — the two numbers the nav rail badges.
 *
 * They used to be literals in `nav.ts` (`badge: 4`, `badge: 2`), which meant the
 * rail claimed four unmatched bank lines on a book with no bank statement at
 * all. Counted now, from the same predicates the two screens filter on.
 */
export interface NavCounts {
  recon: number;
  settlement: number;
}

// ── Account links (Setup → Link Accounts) ────────────────────────────────────

/**
 * One row of Setup → Link Accounts: what a module posts, and where it lands.
 *
 * `key` is `accounts.system_key`, which is the whole mechanism — posting code
 * resolves the key rather than a code, so re-pointing the link re-points every
 * caller at once. `code` is null where the chart carries no account for the key,
 * which is not cosmetic: the next document of that kind fails with a 500 naming
 * it.
 */
export interface AccountLinkRow {
  key: string;
  label: string;
  /** What actually posts, in the words of the document that does it. */
  posts: string;
  side: string;
  /** A control head whose level-5 detail takes the line, not the head itself. */
  isControl: boolean;
  /**
   * How much the link decides.
   *
   * `POSTS` — a line lands here because this key was resolved. `DEFAULT` — a
   * fallback or a starting point; another register usually decides. `UNUSED` —
   * nothing resolves this key today, so re-pointing it changes nothing.
   */
  use: 'POSTS' | 'DEFAULT' | 'UNUSED';
  /** The account types the link may point at. Anything else is refused. */
  accountType: string[];
  code: string | null;
  name: string | null;
  level: number | null;
  isLeaf: boolean | null;
  isActive: boolean | null;
  /** A voucher line on this account would be refused as the chart stands. */
  unpostable: boolean;
}

export interface AccountLinkGroup {
  module: string;
  label: string;
  /** What this module resolves elsewhere — the category, the warehouse, the tax code. */
  note: string | null;
  links: AccountLinkRow[];
}

export interface AccountLinks {
  groups: AccountLinkGroup[];
  /** Keys the chart holds no account for, named rather than counted. */
  missing: string[];
}

export interface AccountLinkWriteResult {
  message: string;
  key: string;
  code: string;
  name: string;
  previousCode: string | null;
}

// ── Permission helper ────────────────────────────────────────────────────────

/**
 * Asks the session, not the role name.
 *
 * The Setup module is hidden by checking `admin.company:VIEW` rather than
 * `roleCode === 'SUPERADMIN'`, so granting Setup to somebody else is a row in
 * `role_permissions` and not a change here. A super administrator short-circuits
 * because `users.is_superadmin` bypasses company grants by definition.
 */
export function useCan(session: Session | undefined) {
  return useMemo(() => {
    const held = new Set(session?.permissions ?? []);
    return (module: string, action = 'VIEW') =>
      Boolean(session?.isSuperAdmin) || held.has(`${module}:${action}`);
  }, [session]);
}
